import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getCustomer, type CustomerDetail } from '../../shared/api/customers.js'
import { canViewCustomers } from '../../shared/utils/permissions.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import OrderStatusBadge from '../orders/components/OrderStatusBadge.js'
import EmptyState from '../menu/components/EmptyState.js'
import { SkeletonOrderRow } from '../../shared/ui/Skeleton.js'

type DetailStatus = 'loading' | 'ready' | 'error' | 'forbidden' | 'notFound'

const PAGE_SIZE = 20

function formatAmount(value: number, currency: string): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`
}

export default function CustomerDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { customerKey } = useParams<{ customerKey: string }>()

  const org = useOrgStore(s => s.activeOrganization)
  const orgId = org?.id
  const canView = canViewCustomers(org?.role)

  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<DetailStatus>('loading')

  const load = useCallback(async (targetPage: number) => {
    if (!orgId || !customerKey) return
    setStatus('loading')
    try {
      const res = await getCustomer(orgId, customerKey, { page: targetPage, pageSize: PAGE_SIZE })
      setCustomer(res)
      setPage(targetPage)
      setStatus('ready')
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 403) setStatus('forbidden')
        else if (err.status === 404) setStatus('notFound')
        else setStatus('error')
      } else {
        setStatus('error')
      }
    }
  }, [orgId, customerKey])

  useEffect(() => {
    if (!canView) {
      setStatus('forbidden')
      return
    }
    void load(1)
  }, [load, canView])

  if (!orgId || !customerKey) return null

  if (!canView || status === 'forbidden') {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('customers.forbidden')}</div>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(ROUTES.CUSTOMERS)}>
            {t('customers.back')}
          </button>
        </div>
      </section>
    )
  }

  if (status === 'loading' && !customer) {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="page-loading">
          {Array.from({ length: 4 }, (_, i) => <SkeletonOrderRow key={i} />)}
        </div>
      </section>
    )
  }

  if (status === 'notFound') {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('customers.notFound')}</div>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(ROUTES.CUSTOMERS)}>
            {t('customers.back')}
          </button>
        </div>
      </section>
    )
  }

  if (status === 'error' || !customer) {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('customers.loadError')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load(page)}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(ROUTES.CUSTOMERS)}>
            {t('customers.back')}
          </button>
          <h2>{customer.name || customer.phone || customer.email || t('app.customers')}</h2>
          <p className="page-section-description">{t('customers.history')}</p>
        </div>
      </div>

      <div className="settings-card">
        <p><strong>{t('customers.phone')}:</strong> {customer.phone || '—'}</p>
        <p><strong>{t('customers.email')}:</strong> {customer.email || '—'}</p>
        <p><strong>{t('customers.ordersCount', { count: customer.ordersCount })}</strong></p>
        <p><strong>{t('customers.totalSpent')}:</strong> {formatAmount(customer.totalSpent, customer.currency)}</p>
        <p><strong>{t('customers.firstOrder')}:</strong> {new Date(customer.firstOrderAt).toLocaleString()}</p>
        <p><strong>{t('customers.lastOrder')}:</strong> {new Date(customer.lastOrderAt).toLocaleString()}</p>
        <p><strong>{t('orders.statusFilter')}:</strong> <OrderStatusBadge status={customer.lastStatus} /></p>
      </div>

      <h3>{t('customers.history')}</h3>
      {customer.orders.length === 0 ? (
        <EmptyState title={t('customers.noOrders')} hint={t('customers.emptyHint')} />
      ) : (
        <>
          <ul className="order-list">
            {customer.orders.map(order => (
              <li
                key={order.id}
                className="order-row"
                onClick={() => navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', order.id))}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter') navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', order.id))
                }}
              >
                <div className="order-row-main">
                  <span className="order-number">{order.orderNumber}</span>
                  <span className="order-row-type">{order.type}</span>
                  <span className="order-row-location">{order.locationName || t('orders.noLocation')}</span>
                </div>
                <span className="order-row-customer">{new Date(order.createdAt).toLocaleString()}</span>
                <div className="order-row-status"><OrderStatusBadge status={order.status} /></div>
                <div className="order-row-total">{formatAmount(order.total, order.currency)}</div>
                <div className="order-row-created">{new Date(order.createdAt).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>

          {customer.meta.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={customer.meta.page <= 1}
                onClick={() => void load(customer.meta.page - 1)}
              >
                {t('customers.previous')}
              </button>
              <span className="pagination-label">
                {t('customers.pageOf', { page: customer.meta.page, total: customer.meta.totalPages })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={customer.meta.page >= customer.meta.totalPages}
                onClick={() => void load(customer.meta.page + 1)}
              >
                {t('customers.next')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
